/**
 * Home / Index marketing page (`/`) — GitHub issue "Create React
 * components for static marketing pages using vite-react-ssg".
 *
 * Statically generated (see `../app/marketingRoutes.ts` / `../app/router.tsx`).
 * No client-side data fetching or auth dependency (Requirement #7): every
 * string is either static copy or resolved from the synchronous i18next
 * bundle in `../lib/i18n`, so this component renders identical, complete
 * markup whether it's server-rendered at build time or hydrated in the
 * browser.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import MarketingLayout from '@/components/marketing/MarketingLayout';
import SeoHead from '@/components/marketing/SeoHead';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

const FEATURE_KEYS = ['screening', 'ranking', 'workflows'] as const;

export default function HomePage(): JSX.Element {
  const { t } = useTranslation();

  return (
    <MarketingLayout>
      <SeoHead
        title={t('marketing.index.title')}
        description={t('marketing.index.description')}
        path="/"
      />

      <section className="container mx-auto px-4 py-20 text-center">
        <h1 className="mx-auto max-w-3xl text-4xl font-bold tracking-tight sm:text-5xl">
          {t('marketing.index.heading')}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-xl text-muted-foreground">
          {t('marketing.index.subheading')}
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Button size="lg" asChild>
            <Link to="/getstarted">{t('marketing.index.cta')}</Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/how-it-works">{t('marketing.index.secondaryCta')}</Link>
          </Button>
        </div>
      </section>

      <section aria-labelledby="home-features-heading" className="border-t bg-muted/30">
        <div className="container mx-auto px-4 py-16">
          <h2 id="home-features-heading" className="text-center text-3xl font-bold">
            {t('marketing.index.featuresHeading')}
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURE_KEYS.map((key) => (
              <Card key={key}>
                <CardHeader>
                  <CardTitle>{t(`marketing.index.features.${key}.title`)}</CardTitle>
                  <CardDescription>
                    {t(`marketing.index.features.${key}.description`)}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
